import classNames from 'classnames';
import React from 'react';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostLinkAccess, PostType } from '../graphql/generated/graphql';
import draftIcon from '../icons/non-prefilled/16x16/drafts.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import publicIcon from '../icons/non-prefilled/16x16/public.svg';
import templateIcon from '../icons/non-prefilled/16x16/template.svg';
import variantIcon from '../icons/non-prefilled/16x16/variant.svg';
import variantDraftIcon from '../icons/non-prefilled/16x16/variantDraft.svg';
import prefilledDraftIcon from '../icons/prefilled/16x16/drafts.svg';
import prefilledPostIcon from '../icons/prefilled/16x16/post.svg';
import postArchivedIcon from '../icons/prefilled/16x16/postArchived.svg';
import templateFilledIcon from '../icons/prefilled/16x16/template.svg';
import templateBaseIcon from '../icons/prefilled/16x16/templateBase.svg';
import templateDraftIcon from '../icons/prefilled/16x16/templateDraft.svg';
import * as styles from './PostIcon.module.scss';

type PostState = {
  type: PostType;
  draft: boolean;
  archived: boolean;
  public: boolean;
};

export const getStateIcon = (
  { type, draft, archived, public: isPublic }: PostState,
  prefilled = false,
) => {
  if (isPublic) {
    return publicIcon;
  }

  switch (type) {
    case PostType.Post: {
      if (draft) return prefilled ? prefilledDraftIcon : draftIcon;
      if (archived) return prefilled ? postArchivedIcon : postIcon;
      return prefilled ? prefilledPostIcon : postIcon;
    }
    case PostType.Template: {
      if (draft) return templateDraftIcon;
      if (archived) return prefilled ? templateBaseIcon : templateIcon;
      return prefilled ? templateFilledIcon : templateIcon;
    }
    case PostType.Variant: {
      if (draft) return variantDraftIcon;
      return variantIcon;
    }
    default: {
      return prefilled ? prefilledPostIcon : postIcon;
    }
  }
};

const getStateText = (state: PostState) => {
  const entity = state.type.toLowerCase();
  if (state.draft) return `draft ${entity}`;
  if (state.archived) return `archived ${entity}`;
  return entity;
};

const GET_POST_STATE_POST_FRAGMENT = gql(`
  fragment getPostState_post on RootPostFields {
    id
    ... on SlimPostFields {
      archivedAt
      publishedAt
      type
      linkAccess
    }
  }
`);

const getPostState = (
  post?: DocumentType<typeof GET_POST_STATE_POST_FRAGMENT> | null,
): PostState => {
  if (post) {
    const type = 'type' in post ? post.type : PostType.Post;
    const isDraft = 'publishedAt' in post ? post.publishedAt == null : false;
    const isArchived = 'archivedAt' in post ? post.archivedAt != null : false;
    const isPublic =
      'linkAccess' in post &&
      (post.linkAccess === PostLinkAccess.Public ||
        post.linkAccess === PostLinkAccess.PublicEdit);

    return {
      type,
      draft: isDraft,
      archived: isArchived,
      public: isPublic,
    };
  }

  return {
    type: PostType.Post,
    draft: false,
    archived: false,
    public: false,
  };
};

const GET_POST_ICON_POST_FRAGMENT = gql(`
  fragment getPostIcon_post on RootPostFields {
    id
    ...getPostState_post
  }
`);

export const getPostIcon = (
  post: DocumentType<typeof GET_POST_ICON_POST_FRAGMENT>,
  prefilled?: boolean,
) => getStateIcon(getPostState(post), prefilled);

const POST_ICON_POST_FRAGMENT = gql(`
  fragment PostIcon_post on RootPostFields {
    id
    ...getPostState_post
  }
`);

interface PostIconProps extends React.ComponentProps<typeof SVGIcon> {
  post: DocumentType<typeof POST_ICON_POST_FRAGMENT>;
}

export const PostIcon = ({ post, ...props }: PostIconProps) => {
  const state = getPostState(post);
  const icon = getStateIcon(state, !state.draft);

  return (
    <SVGIcon
      {...props}
      className={classNames(
        styles.postIcon,
        styles[state.type.toLowerCase()],
        {
          [styles.isDraft]: state.draft,
          [styles.isArchived]: state.archived,
        },
        props.className,
      )}
    >
      {icon}
    </SVGIcon>
  );
};

interface PostIconForStateProps {
  state: PostState;
  prefilled?: boolean;
  bordered?: boolean;
}

export const PostIconForState = ({
  state,
  prefilled,
  bordered,
}: PostIconForStateProps) => {
  return (
    <div
      className={classNames(
        styles.container,
        styles[state.type.toLowerCase()],
        {
          [styles.archived]: state.archived,
          [styles.draft]: state.draft,
          [styles.bordered]: bordered,
        },
      )}
      role="img"
      aria-label={getStateText(state)}
    >
      <SVGIcon>{getStateIcon(state, prefilled)}</SVGIcon>
    </div>
  );
};

const POST_ENTITY_ICON_POST_FRAGMENT = gql(`
  fragment PostEntityIcon_post on RootPostFields {
    id
    ...getPostState_post
    ... on SlimPostFields {
      publishedAt
    }
  }
`);

interface PostEntityIconProps {
  post?: DocumentType<typeof POST_ENTITY_ICON_POST_FRAGMENT>;
  bordered?: boolean;
}

export const PostEntityIcon = ({ post, bordered }: PostEntityIconProps) => {
  const isDraft =
    post && 'publishedAt' in post ? post.publishedAt == null : false;

  const postState = getPostState(post);

  return (
    <PostIconForState
      state={postState}
      prefilled={!isDraft}
      bordered={bordered}
    />
  );
};
